import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { VideoCameraSlash, Warning, List, SignOut, VideoCamera } from '@phosphor-icons/react';

export default function ReporterDashboard() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    authAxios().get('/institutions').then(res => setInstitutions(res.data)).catch(() => toast.error('Kurumlar yüklenemedi'));
  }, [authAxios]);

  const handleStartRecording = () => {
    if (!selectedInstitution) { toast.error('Lütfen bir kurum seçin'); return; }
    if (!consentGiven) {
      setConsentGiven(true);
      toast.info('Kamera ve mikrofon izni gerekecektir. Devam etmek için tekrar basın.');
      return;
    }
    navigate('/reporter/record', { state: { institutionId: selectedInstitution } });
  };

  const selectedInst = institutions.find(i => i.id === selectedInstitution);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Header */}
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Warning weight="fill" className="w-6 h-6 text-[hsl(var(--primary))]" />
          <span className="font-display text-xl text-white">ACİL İHBAR</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="my-reports-button"
            variant="ghost"
            onClick={() => navigate('/reporter/reports')}
            className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10"
          >
            <List weight="bold" className="w-5 h-5 mr-1" />
            <span className="hidden sm:inline">İhbarlarım</span>
          </Button>
          <Button
            data-testid="logout-button"
            variant="ghost"
            onClick={() => { logout(); navigate('/login'); }}
            className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10"
          >
            <SignOut weight="bold" className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6 max-w-lg mx-auto w-full">
        <div className="w-full space-y-6 animate-slide-up">
          {/* Greeting */}
          <div className="text-center">
            <p className="overline text-[hsl(var(--muted-foreground))]">Hoşgeldiniz</p>
            <h2 className="text-2xl text-white font-heading mt-1">{user?.full_name}</h2>
          </div>

          {/* Institution Selector */}
          <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
            <CardContent className="p-6">
              <label className="overline text-[hsl(var(--muted-foreground))] mb-3 block">Kurum Secin</label>
              <Select value={selectedInstitution} onValueChange={setSelectedInstitution}>
                <SelectTrigger data-testid="institution-select" className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white text-base">
                  <SelectValue placeholder="İhbar göndermek istediğiniz kurum" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                  {institutions.map(inst => (
                    <SelectItem key={inst.id} value={inst.id} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white h-12 text-base">
                      {inst.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedInst && (
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{selectedInst.description}</p>
              )}
            </CardContent>
          </Card>

          {/* Warning Text */}
          <div className="bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 rounded-lg p-4">
            <p className="text-sm text-[hsl(var(--primary))] font-medium text-center">
              Kayıt başladıktan sonra 30 saniye sonunda video otomatik gönderilir.
            </p>
          </div>

          {/* Record Button */}
          <div className="flex flex-col items-center gap-4 pt-2">
            <button
              data-testid="record-button"
              onClick={handleStartRecording}
              disabled={!selectedInstitution}
              className={`relative w-40 h-40 rounded-full flex flex-col items-center justify-center transition-all ${
                selectedInstitution
                  ? 'bg-[hsl(var(--primary))] record-button-pulse cursor-pointer active:scale-95'
                  : 'bg-[#2C2C2E] cursor-not-allowed'
              }`}
            >
              {selectedInstitution ? (
                <VideoCamera weight="fill" className="w-14 h-14 text-white" />
              ) : (
                <VideoCameraSlash weight="fill" className="w-14 h-14 text-[#666]" />
              )}
            </button>
            <p className="font-heading text-white text-center text-sm uppercase tracking-wide max-w-[280px]">
              {selectedInstitution ? 'Kayda Başla ve 30 sn Sonunda Otomatik Gönder' : 'Kurum seçin'}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
